import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { RulesService } from '../api/services/rules.service';
import { AppToastService } from '../shared/toast-service/toast-service';
import { AuthService } from '../shared/auth-service/auth.service';
import {
    AdditionalRuleModel,
    DrainageVentilationRuleModel,
    GlazingRuleModel,
    IntegratedRuleModel,
    JointRuleModel,
    LimitationRuleModel,
    MatingRuleModel,
    RuleModel,
    QueryParams,
    SystemModel
} from '../api/models';
import { SystemsService } from '../api/services/systems.service';
import { ArticlesService } from '../api/services/articles.service';

type RuleType = 'additional' | 'drainage-ventilation' | 'glazing' | 'integrated' | 'joint' | 'limitation' | 'mating';

type RuleModelType = AdditionalRuleModel | DrainageVentilationRuleModel | GlazingRuleModel |
    IntegratedRuleModel | JointRuleModel | LimitationRuleModel | MatingRuleModel;
type existingRules = AdditionalRuleModel[] | DrainageVentilationRuleModel[] | GlazingRuleModel[] |
    IntegratedRuleModel[] | JointRuleModel[] | LimitationRuleModel[] | MatingRuleModel[];

interface RuleTypeConfig {
    displayName: string;
    routeName: string;
    serviceMethod: string;
}

@Component({
    selector: 'app-rules-management',
    templateUrl: './rules-management.component.html',
    styleUrls: ['./rules-management.component.scss'],
    standalone: false,
})
export class RulesManagementComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    // Component state
    ruleType: RuleType = 'additional';
    displayName: string = '';
    loading = false;
    loggedIn = false;

    // Data
    rules: RuleModelType[] = [];
    filteredRules: RuleModelType[] = [];
    currentRule: RuleModelType | null = null;

    // System filter (for mating rules)
    systems: SystemModel[] = [];
    selectedSystemId: string = '';

    // Article ID search (for mating rules)
    searchArticleId: string = '';

    // Duplicate article ID tracking (for mating rules)
    duplicateArticleIds: Map<string, string[]> = new Map(); // articleId -> array of rule IDs
    articleIdColors: Map<string, string> = new Map(); // articleId -> color
    readonly duplicateColors: string[] = [
        '#FF6B6B', // Bright red
        '#4ECDC4', // Turquoise
        '#45B7D1', // Sky blue
        '#FFA07A', // Light salmon
        '#98D8C8', // Mint green
        '#F7DC6F', // Yellow
        '#BB8FCE', // Lavender
        '#85C1E2', // Light blue
        '#F8B739', // Orange
        '#82E0AA', // Green
        '#F1948A', // Pink
        '#85929E', // Gray-blue
        '#F39C12', // Dark orange
        '#1ABC9C', // Teal
        '#3498DB', // Blue
        '#E74C3C', // Red
        '#9B59B6', // Purple
        '#16A085'  // Dark teal
    ];

    // Sorting
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    // UI state
    showCreateForm = false;
    showEditForm = false;
    showDeleteConfirm = false;
    ruleToDelete: RuleModelType | null = null;

    // Form data
    newRule: RuleModelType = {} as RuleModelType;
    editingRule: RuleModelType = {} as RuleModelType;
    existingRules: RuleModelType[] = [];

    // Rule type configurations
    private ruleTypeConfigs: Record<RuleType, RuleTypeConfig> = {
        'additional': {
            displayName: 'Additional Rules',
            routeName: 'additional',
            serviceMethod: 'Additional'
        },
        'drainage-ventilation': {
            displayName: 'Drainage Ventilation Rules',
            routeName: 'drainage-ventilation',
            serviceMethod: 'DrainageVentilation'
        },
        'glazing': {
            displayName: 'Glazing Rules',
            routeName: 'glazing',
            serviceMethod: 'Glazing'
        },
        'integrated': {
            displayName: 'Integrated Rules',
            routeName: 'integrated',
            serviceMethod: 'Integrated'
        },
        'joint': {
            displayName: 'Joint Rules',
            routeName: 'joint',
            serviceMethod: 'Joint'
        },
        'limitation': {
            displayName: 'Limitation Rules',
            routeName: 'limitation',
            serviceMethod: 'Limitation'
        },
        'mating': {
            displayName: 'Mating Rules',
            routeName: 'mating',
            serviceMethod: 'Mating'
        }
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private rulesService: RulesService,
        private toastService: AppToastService,
        private authService: AuthService,
        private systemsService: SystemsService
    ) { }

    ngOnInit(): void {
        this.loggedIn = !!this.authService.getToken();

        // Get rule type from route
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const routeType = params['type'] as string;
            // Validate that the route type is a valid RuleType
            if (routeType && this.ruleTypeConfigs[routeType as RuleType]) {
                this.ruleType = routeType as RuleType;
                this.displayName = this.ruleTypeConfigs[this.ruleType].displayName;
                // Load systems if this is a mating rules page
                if (this.ruleType === 'mating' || this.ruleType === 'joint') {
                    this.loadSystems();
                }
                this.loadRules();
            } else {
                // Invalid rule type, redirect to rules dashboard
                this.toastService.show('Invalid rule type', {
                    classname: 'bg-warning text-dark',
                    delay: 3000
                });
                this.router.navigate(['/rules-dashboard']);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    logout(): void {
        this.authService.logout();
    }

    loadRules(): void {
        if (!this.ruleType) return;

        this.loading = true;
        // Load all rules by using a large page size
        const params: QueryParams = {
            page: 1,
            pageSize: 10000
        };

        const serviceMethod = `get${this.ruleTypeConfigs[this.ruleType].serviceMethod}Rules`;

        (this.rulesService as any)[serviceMethod](params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    // The service unwraps ApiResponse, so we receive the paged response model directly
                    // Handle both camelCase (new) and PascalCase (old) formats
                    let data: any[] = [];

                    // Check if response is already an array (shouldn't happen, but handle it)
                    if (Array.isArray(response)) {
                        data = response;
                    } else if (response && typeof response === 'object') {
                        // Response is the paged response model
                        data = response.data || response.Data || [];
                    }
                    console.log('Loaded rules data:', data);

                    // Transform rules if they contain PascalCase properties (especially for mating rules)
                    if (this.ruleType === 'mating' && data.length > 0) {
                        data = data.map(rule => this.transformMatingRuleIfNeeded(rule));
                    }

                    // Transform rules if they contain PascalCase properties (especially for joint rules)
                    if (this.ruleType === 'joint' && data.length > 0) {
                        data = data.map(rule => this.transformJointRuleIfNeeded(rule));
                    }

                    this.rules = data || [];
                    this.existingRules = this.rules;
                    // Initialize filteredRules with all rules, then apply filter if needed
                    this.filteredRules = [...this.rules];
                    this.applyFilters();
                    this.applySorting();
                    // Analyze duplicate article IDs if this is a mating rules page
                    if (this.ruleType === 'mating' || this.ruleType === 'joint') {
                        this.analyzeDuplicateArticleIds();
                    }
                    this.loading = false;
                },
                error: (error: any) => {
                    console.error('Error loading rules:', error);
                    this.toastService.show('Error loading rules', {
                        classname: 'bg-danger text-white',
                        delay: 3000
                    });
                    this.loading = false;
                }
            });
    }

    onCreateNew(): void {
        this.toastService.clear();
        this.newRule = {} as RuleModelType;
        this.showCreateForm = true;
        this.showEditForm = false;
    }

    onEdit(rule: RuleModelType): void {
        // For mating rules, navigate to edit route
        if (this.ruleType === 'mating' && rule.id) {
            this.router.navigate(['/rules/mating/edit', rule.id]);
            return;
        }

        // For other rule types, show modal
        // rule = this.ruleType === 'joint' ? this.normalizeJointResponse(rule) : rule;
        // this.editingRule = { ...rule };
        // this.showEditForm = true;
        // this.showCreateForm = false;
        if (this.ruleType === 'joint' && rule.id) {
            this.router.navigate(['/rules/joint/edit', rule.id]);
            return;
        }
    }

    onDelete(rule: RuleModelType): void {
        this.ruleToDelete = rule;
        this.showDeleteConfirm = true;
    }

    confirmDelete(): void {
        if (!this.ruleToDelete?.id) return;

        const serviceMethod = `delete${this.ruleTypeConfigs[this.ruleType].serviceMethod}Rule`;

        (this.rulesService as any)[serviceMethod](this.ruleToDelete.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.toastService.show('Rule deleted successfully', {
                        classname: 'bg-success text-white',
                        delay: 3000
                    });
                    this.showDeleteConfirm = false;
                    this.ruleToDelete = null;
                    this.loadRules();
                },
                error: (error: any) => {
                    console.error('Error deleting rule:', error);
                    this.toastService.show('Error deleting rule', {
                        classname: 'bg-danger text-white',
                        delay: 3000
                    });
                }
            });
    }

    cancelDelete(): void {
        this.showDeleteConfirm = false;
        this.ruleToDelete = null;
    }

    saveNewRule(): void {
        // This method is now handled by the rule form component
        // Keep for backward compatibility but it won't be called
    }

    saveEditedRule(): void {
        // This method is now handled by the rule form component
        // Keep for backward compatibility but it won't be called
    }

    // private mapPayloadForApi(ruleData: any): JointRuleModel {
    //     switch (this.ruleType) {
    //         case 'joint':
    //             return {
    //                 id: ruleData.id,
    //                 ruleId: ruleData['ruleId'] || '',
    //                 name: ruleData['name'] || '',
    //                 description: ruleData['description'] || '',
    //                 jointSections: ruleData.jointSections || [], //  Map Joints → jointSections
    //                 createdAt: ruleData.createdAt,
    //                 updatedAt: ruleData.updatedAt
    //             };
    //         default:
    //             return ruleData;
    //     }
    // }

    private mapPayloadForApi(ruleData: any): JointRuleModel | MatingRuleModel {
        switch (this.ruleType) {
            case 'joint':
                return {
                    id: ruleData.Id || ruleData.id || '',
                    ruleId: ruleData.JointId || ruleData.ruleId || '',
                    name: ruleData.Name || ruleData.name || '',
                    description: ruleData.Description || ruleData.description || '',
                    systemId: ruleData.SystemId || ruleData.systemId || '',
                    jointSections: ruleData.jointSections?.map((s: any) => ({
                        id: s.Id || '',
                        description: s.Description || s.description || '',
                        material: s.Material || s.material || '',
                        type: s.Type || s.type || '',
                        imageUrl: s.ImageUrl || s.imageUrl || '',
                        articles: s.Articles || s.articles || []
                    })) || [],
                    createdAt: ruleData.createdAt,
                    updatedAt: ruleData.updatedAt
                };
            case 'mating':
                return {
                    id: ruleData.Id || ruleData.id || '',
                    ruleId: ruleData.RuleId || ruleData.ruleId || '',
                    name: ruleData.Name || ruleData.name || '',
                    description: ruleData.Description || ruleData.description || '',
                    systemId: ruleData.SystemId || ruleData.systemId || '',
                    crossSections: ruleData.crossSections?.map((s: any) => ({
                        id: s.Id || s.id || '',
                        description: s.Description || s.description || '',
                        material: s.Material || s.material || '',
                        type: s.Type || s.type || '',
                        imageUrl: s.ImageUrl || s.imageUrl || '',
                        // Map articles: preserve id if present, send articleName/material/type
                        articles: (s.Articles || s.articles || []).map((a: any) => ({
                            id: (a as any).id || '',
                            articleId: (a as any).articleId || '',
                            articleName: (a as any).articleName || '',
                            material: (a as any).material || '',
                            type: (a as any).type || ''
                        })),

                        articleIds: s.ArticleIds || s.articleIds || [],
                        transformation: {
                            positionX: s.PositionX ?? s.positionX ?? 0,
                            positionY: s.PositionY ?? s.positionY ?? 0,
                            positionZ: s.PositionZ ?? s.positionZ ?? 0,
                            rotation: s.Rotation ?? s.rotation ?? 0,
                            scaleX: s.ScaleX ?? s.scaleX ?? 1,
                            scaleY: s.ScaleY ?? s.scaleY ?? 1,
                            isMirrored: s.IsMirrored ?? s.isMirrored ?? false
                        }
                    })) || [],
                    createdAt: ruleData.createdAt,
                    updatedAt: ruleData.updatedAt
                };
            default:
                return ruleData;
        }
    }

    private mapJointRule(ruleData: any): JointRuleModel {
        const joint = ruleData.Joints[0]; // you are adding only one Joint

        return {
            id: ruleData.Id || '',
            ruleId: joint.jointId,
            name: joint.name,
            description: joint.description,
            jointSections: joint.jointSections.map((s: any) => ({
                Id: s.id,
                Description: s.description,
                Material: s.material,
                Type: s.type,
                ImageUrl: s.imageUrl,
                Articles: s.articles || []
            })),
            createdAt: ruleData.createdAt,
            updatedAt: ruleData.updatedAt
        };
    }

    // private mapPayloadForApi(ruleData: any): any {
    //     switch (this.ruleType) {
    //         case 'joint':
    //             return this.mapJointRule(ruleData);
    //         default:
    //             return ruleData; // fallback (if no mapping required)
    //     }
    // }

    private normalizeJointResponse(apiResponse: any) {
        if (!apiResponse) return apiResponse;

        return {
            ...apiResponse,
            joints: [
                {
                    id: apiResponse.id,
                    ruleId: apiResponse.ruleId,
                    name: apiResponse.name,
                    systemId: apiResponse.systemId,
                    description: apiResponse.description,
                    imageUrl: apiResponse.imageUrl || '',
                    jointSections: apiResponse.jointSections || []
                }
            ]
        };
    }

    private normalizeMatingRulResponse(apiResponse: any) {
        if (!apiResponse) return apiResponse;

        return {
            ...apiResponse,
            sections: [
                {
                    id: apiResponse.id,
                    sectionId: apiResponse.ruleId,
                    name: apiResponse.name,
                    description: apiResponse.description,
                    systemId: apiResponse.systemId || '',
                    imageUrl: apiResponse.imageUrl || '',
                    crossSections: apiResponse.crossSections || []
                }
            ]
        };
    }

    onRuleFormSave(event: { rule: RuleModelType; system: SystemModel }): void {
        let ruleData = event.rule;
        let systemData = event.system;

        console.log("Received rule:", ruleData);
        console.log("Received system:", systemData);

        // 1. Determine which rule type is being saved (Joint, Item, Section, etc.)
        const config = this.ruleTypeConfigs[this.ruleType];
        const serviceMethod = `create${this.ruleTypeConfigs[this.ruleType].serviceMethod}Rule`;



        // 2️. Transform the payload BEFORE sending to API
        const apiPayload = this.mapPayloadForApi(ruleData);

        // 3️. Call the correct service method dynamically
        (this.rulesService as any)[serviceMethod](apiPayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    let normalized = res;

                    // Convert backend to frontend only for joint
                    if (this.ruleType === 'joint') {
                        normalized = this.normalizeJointResponse(res);
                    }
                    if (this.ruleType === 'mating') {
                        normalized = this.normalizeMatingRulResponse(res);

                    }

                    // Update local ruleData so edit mode works correctly
                    ruleData = normalized;
                    // Only attempt to update the system if we have a valid id
                    if (systemData?.id) {

                        switch (this.ruleType) {
                            case 'mating':
                                systemData.matingRules ??= [];
                                systemData.matingRules.push(normalized);
                                break;

                            case 'joint':
                                systemData.jointRules ??= [];
                                systemData.jointRules.push(normalized);
                                break;
                        }

                        this.systemsService.updateSystem(systemData.id, systemData)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                                next: (response) => {

                                },
                                error: (error) => {

                                }
                            });
                    } else {
                        console.warn('No system id provided; skipping system update.');
                    }

                    // Clear previous toasts and show success message
                    // this.toastService.clear();
                    // this.toastService.show('Rule created successfully', {
                    //     classname: 'bg-success text-white',
                    //     delay: 3000
                    // });
                    // this.showCreateForm = false;
                    // this.newRule = {} as RuleModelType;
                    // this.loadRules();

                    this.showCreateForm = false;
                    this.newRule = {} as RuleModelType;

                    setTimeout(() => {
                        this.toastService.clear();
                        this.toastService.show('Rule created successfully', {
                            classname: 'bg-success text-white',
                            delay: 3000
                        });
                        this.loadRules();
                    }, 0);
                },
                error: (error: any) => {
                    console.error('Error creating rule:', error);
                    // Clear previous toasts and show success message
                    this.toastService.clear();
                    this.toastService.show('Error creating rule:' + error.Message, {
                        classname: 'bg-danger text-white',
                        delay: 3000
                    });
                }
            });
    }

    onRuleFormUpdate(event: { rule: RuleModelType; system: SystemModel }): void {
        let ruleData = event.rule;
        let systemData = event.system;

        const ruleId = (ruleData as any).id || (ruleData as any).Id;
        if (!ruleId) return;

        const config = this.ruleTypeConfigs[this.ruleType];
        const serviceMethod = `update${this.ruleTypeConfigs[this.ruleType].serviceMethod}Rule`;

        // 2️. Transform the payload BEFORE sending to API
        const apiPayload = this.mapPayloadForApi(ruleData);

        (this.rulesService as any)[serviceMethod](ruleId, apiPayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    let normalized = res;

                    // Convert backend to frontend only for joint
                    if (this.ruleType === 'joint') {
                        normalized = this.normalizeJointResponse(res);
                    }
                    if (this.ruleType === 'mating') {
                        normalized = this.normalizeMatingRulResponse(res);

                    }

                    // Update local ruleData so edit mode works correctly
                    ruleData = normalized;
                    // Only attempt to update the system if we have a valid id
                    if (systemData?.id) {

                        switch (this.ruleType) {
                            case 'mating':
                                systemData.matingRules ??= [];
                                systemData.matingRules.push(normalized);
                                break;

                            case 'joint':
                                systemData.jointRules ??= [];
                                systemData.jointRules.push(normalized);
                                break;
                        }

                        this.systemsService.updateSystem(systemData.id, systemData)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                                next: (response) => {

                                },
                                error: (error) => {

                                }
                            });
                    } else {
                        console.warn('No system id provided; skipping system update.');
                    }

                    // Clear previous toasts and show success message
                    // this.toastService.clear();
                    // this.toastService.show('Rule updated successfully', {
                    //     classname: 'bg-success text-white',
                    //     delay: 3000
                    // });
                    // this.showEditForm = false;
                    // this.editingRule = {} as RuleModelType;
                    // this.loadRules();
                    this.showCreateForm = false;
                    this.editingRule = {} as RuleModelType;

                    setTimeout(() => {
                        this.toastService.clear();
                        this.toastService.show('Rule updated successfully', {
                            classname: 'bg-success text-white',
                            delay: 3000
                        });
                        this.loadRules();
                    }, 0);
                },
                error: (error: any) => {
                    console.error('Error updating rule:', error);
                    // Clear previous toasts and show success message
                    this.toastService.clear();
                    this.toastService.show('Error updating rule', {
                        classname: 'bg-danger text-white',
                        delay: 3000
                    });
                }
            });
    }

    cancelCreate(): void {
        this.showCreateForm = false;
        this.newRule = {} as RuleModelType;
    }

    cancelEdit(): void {
        this.showEditForm = false;
        this.editingRule = {} as RuleModelType;
    }

    // These methods are kept for backward compatibility but are no longer used
    // The rule form component now handles these operations
    addRuleToCollection(rule: RuleModelType): void {
        // Deprecated: Handled by rule form component
    }

    removeRuleFromCollection(rule: RuleModelType, index: number): void {
        // Deprecated: Handled by rule form component
    }

    getCrossSectionsCount(rule: RuleModelType): number {
        switch (this.ruleType) {
            case 'additional':
                return (rule as AdditionalRuleModel).rules?.length || 0;
            case 'drainage-ventilation':
                return (rule as DrainageVentilationRuleModel).items?.length || 0;
            case 'glazing':
                return (rule as GlazingRuleModel).items?.length || 0;
            case 'integrated':
                return (rule as IntegratedRuleModel).items?.length || 0;
            case 'joint':
                return (rule as JointRuleModel).jointSections?.length || 0;
            case 'limitation':
                return (rule as LimitationRuleModel).items?.length || 0;
            case 'mating':
                return (rule as MatingRuleModel).crossSections?.length || 0;
            default:
                return 0;
        }
    }

    private validateRule(rule: RuleModelType): boolean {
        const count = this.getCrossSectionsCount(rule);
        if (count === 0) {
            this.toastService.show('At least one rule must be added', {
                classname: 'bg-warning text-dark',
                delay: 3000
            });
            return false;
        }

        // For now, just check if there's at least one item
        // More specific validation can be added per rule type if needed

        return true;
    }

    getRuleName(rule: RuleModelType): string {
        return (rule as any).name || (rule as any).Name || 'Unnamed Rule';
    }

    // Transform mating rule from PascalCase to camelCase if needed
    private transformMatingRuleIfNeeded(rule: any): MatingRuleModel {
        if (!rule) return rule;

        // Check if transformation is needed (has PascalCase properties)
        const needsTransformation = rule.Sections !== undefined || rule.Id !== undefined ||
            rule.CreatedAt !== undefined || rule.UpdatedAt !== undefined;

        if (needsTransformation) {
            // Transform from PascalCase to camelCase
            const transformed: MatingRuleModel = {
                id: rule.Id || rule.id,
                crossSections: (rule.CrossSections || rule.crossSections || []).map((section: any) => this.transformSectionIfNeeded(section)),
                ruleId: rule.SectionId || rule.sectionId || rule.RuleId,
                name: rule.Name || rule.name,
                description: rule.Description || rule.description,
                systemId: rule.SystemId || rule.systemId,
                createdAt: rule.CreatedAt || rule.createdAt,
                updatedAt: rule.UpdatedAt || rule.updatedAt
            };
            return transformed;
        }

        // Already in camelCase, but check nested sections for transformation
        if (rule.crossSections && Array.isArray(rule.crossSections)) {
            rule.crossSections = rule.crossSections.map((section: any) => this.transformSectionIfNeeded(section));
        }

        return rule;
    }

    // Transform section from PascalCase to camelCase if needed
    private transformSectionIfNeeded(section: any): any {
        if (!section) return section;

        // Check if transformation is needed (has PascalCase properties)
        const needsTransformation =
            section.Description !== undefined || section.ImageUrl !== undefined ||
            section.Material !== undefined || section.Type !== undefined || section.Articles !== undefined ||
            section.ArticleIds !== undefined || section.Transformation !== undefined;

        if (needsTransformation) {
            // Transform from PascalCase to camelCase
            return {
                id: section.Id || section.id,
                description: section.Description || section.description,
                imageUrl: section.ImageUrl || section.imageUrl,
                material: section.Material || section.material,
                type: section.Type || section.type,
                articles: section.Articles || section.articles || [],
                articleIds: section.ArticleIds || section.articleIds || [],
                transformation: section.Transformation ? {
                    positionX: section.Transformation.PositionX ?? section.Transformation.positionX ?? 0,
                    positionY: section.Transformation.PositionY ?? section.Transformation.positionY ?? 0,
                    positionZ: section.Transformation.PositionZ ?? section.Transformation.positionZ ?? 0,
                    rotation: section.Transformation.Rotation ?? section.Transformation.rotation ?? 0,
                    scaleX: section.Transformation.ScaleX ?? section.Transformation.scaleX ?? 1,
                    scaleY: section.Transformation.ScaleY ?? section.Transformation.scaleY ?? 1,
                    isMirrored: section.Transformation.IsMirrored ?? section.Transformation.isMirrored ?? false
                } : section.transformation
            };
        }

        // Already in camelCase, but ensure articleIds is an array
        return {
            ...section,
            articles: section.articles || [],
            articleIds: section.articleIds || []
        };
    }
    // Transform joint rule from PascalCase to camelCase if needed
    private transformJointRuleIfNeeded(rule: any): JointRuleModel {
        if (!rule) return rule;

        // Check if transformation is needed (has PascalCase properties)
        const needsTransformation = rule.JointSections !== undefined || rule.Id !== undefined ||
            rule.CreatedAt !== undefined || rule.UpdatedAt !== undefined;

        if (needsTransformation) {
            // Transform from PascalCase to camelCase
            const transformed: JointRuleModel = {
                id: rule.Id || rule.id,
                jointSections: (rule.JointSections || rule.jointSections || []).map((section: any) => this.transformJointSectionIfNeeded(section)),
                ruleId: rule.RuleId || rule.ruleId,
                name: rule.Name || rule.name,
                systemId: rule.SystemId || rule.systemId,
                description: rule.Description || rule.description,
                createdAt: rule.CreatedAt || rule.createdAt,
                updatedAt: rule.UpdatedAt || rule.updatedAt
            };
            return transformed;
        }

        // Already in camelCase, but check nested sections for transformation
        if (rule.jointSections && Array.isArray(rule.jointSections)) {
            rule.jointSections = rule.jointSections.map((section: any) => this.transformJointSectionIfNeeded(section));
        }

        return rule;
    }

    // Transform section from PascalCase to camelCase if needed
    private transformJointSectionIfNeeded(section: any): any {
        if (!section) return section;

        // Check if transformation is needed (has PascalCase properties)
        const needsTransformation = section.id !== undefined || section.Id !== undefined ||
            section.Description !== undefined || section.ImageUrl !== undefined ||
            section.Material !== undefined || section.Type !== undefined || section.Articles !== undefined;

        if (needsTransformation) {
            // Transform from PascalCase to camelCase
            return {
                id: section.Id || section.id,
                description: section.Description || section.description,
                imageUrl: section.ImageUrl || section.imageUrl,
                material: section.Material || section.material,
                type: section.Type || section.type,
                articles: section.Articles || section.articles,
                articleIds: section.ArticleIds || section.articleIds || []
            };
        }

        // Already in camelCase
        return section;
    }

    // Load systems for the filter dropdown (only for mating rules)
    loadSystems(): void {
        this.systemsService.getSystems({ page: 1, pageSize: 1000 })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    const data = response.data || response.Data || [];
                    this.systems = Array.isArray(data)
                        ? data.map((system: any) => this.transformSystemIfNeeded(system))
                        : [];
                },
                error: (error: any) => {
                    console.error('Error loading systems:', error);
                }
            });
    }

    // Transform system object from PascalCase to camelCase if needed
    private transformSystemIfNeeded(system: any): SystemModel {
        if (!system) return system;
        if (typeof system !== 'object') return system;

        // Check if already in camelCase
        if (system.name !== undefined || system.id !== undefined) {
            return { ...system };
        }

        // Transform from PascalCase to camelCase
        return {
            id: system.Id || system.id,
            name: system.Name || system.name || '',
            version: system.Version || system.version,
            description: system.Description || system.description,
            material: system.Material || system.material,
            type: system.Type || system.type,
            createdAt: system.CreatedAt || system.createdAt,
            updatedAt: system.UpdatedAt || system.updatedAt
        };
    }

    // Handle system filter change
    onSystemFilterChange(systemId: string): void {
        this.selectedSystemId = systemId;
        this.applyFilters();
    }

    // Handle article ID search change
    onArticleIdSearchChange(searchText: string): void {
        this.searchArticleId = searchText.trim();
        console.log('Article ID search changed:', this.searchArticleId);
        this.applyFilters();
    }

    // Apply all filters (system filter and article ID search) to rules
    applyFilters(): void {
        if (this.ruleType !== 'mating' && this.ruleType !== 'joint') {
            this.filteredRules = [...this.rules];
            this.applySorting();
            return;
        }

        let filtered = [...this.rules];

        // Apply system filter
        if (this.selectedSystemId) {
            if (this.ruleType === 'mating') {
                filtered = filtered.filter((rule: RuleModelType) => {
                    const matingRule = rule as MatingRuleModel;
                    return matingRule.systemId === this.selectedSystemId;
                });
            }
            if (this.ruleType === 'joint') {
                filtered = filtered.filter((rule: RuleModelType) => {
                    const jointRule = rule as JointRuleModel;
                    return jointRule.systemId === this.selectedSystemId;
                });
            }
        }

        // Apply article ID search filter (only for mating rules)
        if (this.ruleType === 'mating' && this.searchArticleId) {
            const searchTerm = this.searchArticleId.toLowerCase().trim();
            console.log('Applying article ID search filter with term:', searchTerm);
            console.log('Rules before article filter:', filtered.length);

            filtered = filtered.filter((rule: RuleModelType) => {
                const matingRule = rule as MatingRuleModel;
                if (!matingRule.crossSections || matingRule.crossSections.length === 0) {
                    return false;
                }

                // Check if any cross section contains the searched article ID
                const matches = matingRule.crossSections.some((crossSection: any) => {
                    // Check in articleIds array
                    const articleIds = crossSection.articleIds || crossSection.ArticleIds || [];
                    if (Array.isArray(articleIds) && articleIds.length > 0) {
                        const foundInIds = articleIds.some((articleId: any) => {
                            if (!articleId) return false;
                            const idStr = String(articleId).toLowerCase();
                            return idStr.includes(searchTerm);
                        });
                        if (foundInIds) {
                            console.log('Found in articleIds for rule:', matingRule.id, 'articleIds:', articleIds);
                            return true;
                        }
                    }

                    // Check in articles array (by article.id or article.Id)
                    const articles = crossSection.articles || crossSection.Articles || [];
                    if (Array.isArray(articles) && articles.length > 0) {
                        const foundInArticles = articles.some((article: any) => {
                            if (!article) return false;
                            // Check article.id or article.Id (handle both PascalCase and camelCase)
                            const articleId = article.id || article.Id || '';
                            if (articleId) {
                                const idStr = String(articleId).toLowerCase();
                                if (idStr.includes(searchTerm)) {
                                    console.log('Found in articles.id for rule:', matingRule.id, 'article.id:', articleId);
                                    return true;
                                }
                            }
                            // Also check articleName as a fallback
                            const articleName = article.articleName || article.ArticleName || '';
                            if (articleName) {
                                const nameStr = String(articleName).toLowerCase();
                                if (nameStr.includes(searchTerm)) {
                                    console.log('Found in articles.articleName for rule:', matingRule.id, 'articleName:', articleName);
                                    return true;
                                }
                            }
                            return false;
                        });
                        if (foundInArticles) return true;
                    }

                    return false;
                });

                if (matches) {
                    console.log('Rule matches search:', matingRule.id, matingRule.name);
                }
                return matches;
            });

            console.log('Rules after article filter:', filtered.length);
        }

        // Apply article ID search filter for Joint Rules
        if (this.ruleType === 'joint' && this.searchArticleId) {
            const searchTerm = this.searchArticleId.toLowerCase().trim();
            console.log('Applying article ID search filter with term:', searchTerm);
            console.log('Rules before article filter:', filtered.length);

            filtered = filtered.filter((rule: RuleModelType) => {
                const jointRule = rule as JointRuleModel;
                if (!jointRule.jointSections || jointRule.jointSections.length === 0) {
                    return false;
                }

                // Check if any cross section contains the searched article ID
                const matches = jointRule.jointSections.some((jointSection: any) => {
                    // Check in articleIds array
                    const articleIds = jointSection.articleIds || jointSection.ArticleIds || [];
                    if (Array.isArray(articleIds) && articleIds.length > 0) {
                        const foundInIds = articleIds.some((articleId: any) => {
                            if (!articleId) return false;
                            const idStr = String(articleId).toLowerCase();
                            return idStr.includes(searchTerm);
                        });
                        if (foundInIds) {
                            console.log('Found in articleIds for rule:', jointRule.id, 'articleIds:', articleIds);
                            return true;
                        }
                    }

                    // Check in articles array (by article.id or article.Id)
                    const articles = jointSection.articles || jointSection.Articles || [];
                    if (Array.isArray(articles) && articles.length > 0) {
                        const foundInArticles = articles.some((article: any) => {
                            if (!article) return false;
                            // Check article.id or article.Id (handle both PascalCase and camelCase)
                            const articleId = article.id || article.Id || '';
                            if (articleId) {
                                const idStr = String(articleId).toLowerCase();
                                if (idStr.includes(searchTerm)) {
                                    console.log('Found in articles.id for rule:', jointRule.id, 'article.id:', articleId);
                                    return true;
                                }
                            }
                            // Also check articleName as a fallback
                            const articleName = article.articleName || article.ArticleName || '';
                            if (articleName) {
                                const nameStr = String(articleName).toLowerCase();
                                if (nameStr.includes(searchTerm)) {
                                    console.log('Found in articles.articleName for rule:', jointRule.id, 'articleName:', articleName);
                                    return true;
                                }
                            }
                            return false;
                        });
                        if (foundInArticles) return true;
                    }

                    return false;
                });

                if (matches) {
                    console.log('Rule matches search:', jointRule.id, jointRule.name);
                }
                return matches;
            });

            console.log('Rules after article filter:', filtered.length);
        }

        this.filteredRules = filtered;
        this.applySorting();

        // Note: Duplicate analysis is done on all rules (this.rules), not filtered rules
        // This ensures we detect duplicates across the entire dataset
        // The highlighting will still work on filtered rules based on the full analysis
    }

    // Get system name by ID
    getSystemName(systemId: string): string {
        console.log('Getting system name for ID:', systemId);
        if (!systemId) return 'No System';
        const system = this.systems.find(s => s.id === systemId);
        return system?.name || 'Unknown System';
    }

    // Get system ID from a rule (for mating rules)
    getRuleSystemId(rule: RuleModelType): string | undefined {
        if (this.ruleType === 'mating') {
            return (rule as MatingRuleModel).systemId;
        }
        else if (this.ruleType === 'joint') {
            return (rule as JointRuleModel).systemId;
        } else
            return undefined;
    }

    // Analyze duplicate article IDs across all mating rules
    analyzeDuplicateArticleIds(): void {
        if (this.ruleType !== 'mating' && this.ruleType !== 'joint') {
            this.duplicateArticleIds.clear();
            this.articleIdColors.clear();
            return;
        }


        // Map to track which rules contain each article ID
        const articleIdToRules = new Map<string, Set<string>>();
        console.log('Analyzing duplicate article IDs for rules:', this.rules);

        if (this.ruleType === 'mating') {
            // Iterate through all mating rules
            this.rules.forEach((rule) => {
                const matingRule = rule as MatingRuleModel;
                if (!matingRule.id || !matingRule.crossSections) return;

                // Collect all article IDs from all cross sections in this rule
                const ruleArticleIds = new Set<string>();

                matingRule.crossSections.forEach((crossSection: any) => {
                    // Check articleIds array
                    const articleIds = crossSection.articleIds || crossSection.ArticleIds || [];
                    if (Array.isArray(articleIds)) {
                        articleIds.forEach((articleId: any) => {
                            if (articleId) {
                                const idStr = String(articleId).trim();
                                if (idStr) {
                                    ruleArticleIds.add(idStr);
                                }
                            }
                        });
                    }

                    // Check articles array
                    const articles = crossSection.articles || crossSection.Articles || [];
                    if (Array.isArray(articles)) {
                        articles.forEach((article: any) => {
                            if (article) {
                                const articleId = article.id || article.Id || '';
                                if (articleId) {
                                    const idStr = String(articleId).trim();
                                    if (idStr) {
                                        ruleArticleIds.add(idStr);
                                    }
                                }
                            }
                        });
                    }
                });

                // Add each article ID to the map with the rule ID
                ruleArticleIds.forEach((articleId) => {
                    if (!articleIdToRules.has(articleId)) {
                        articleIdToRules.set(articleId, new Set<string>());
                    }
                    articleIdToRules.get(articleId)!.add(matingRule.id!);
                });
            });
        }

        if (this.ruleType === 'joint') {
            // Iterate through all joint rules
            this.rules.forEach((rule) => {
                const jointRule = rule as JointRuleModel;
                if (!jointRule.id || !jointRule.jointSections) return;

                // Collect all article IDs from all joint sections in this rule
                const ruleArticleIds = new Set<string>();

                jointRule.jointSections.forEach((jointSection: any) => {
                    // Check articleIds array
                    const articleIds = jointSection.articleIds || jointSection.ArticleIds || [];
                    if (Array.isArray(articleIds)) {
                        articleIds.forEach((articleId: any) => {
                            if (articleId) {
                                const idStr = String(articleId).trim();
                                if (idStr) {
                                    ruleArticleIds.add(idStr);
                                }
                            }
                        });
                    }

                    // Check articles array
                    const articles = jointSection.articles || jointSection.Articles || [];
                    if (Array.isArray(articles)) {
                        articles.forEach((article: any) => {
                            if (article) {
                                const articleId = article.id || article.Id || '';
                                if (articleId) {
                                    const idStr = String(articleId).trim();
                                    if (idStr) {
                                        ruleArticleIds.add(idStr);
                                    }
                                }
                            }
                        });
                    }
                });

                // Add each article ID to the map with the rule ID
                ruleArticleIds.forEach((articleId) => {
                    if (!articleIdToRules.has(articleId)) {
                        articleIdToRules.set(articleId, new Set<string>());
                    }
                    articleIdToRules.get(articleId)!.add(jointRule.id!);
                });
            });
        }



        // Find article IDs that appear in more than one rule
        this.duplicateArticleIds.clear();
        this.articleIdColors.clear();

        let colorIndex = 0;
        articleIdToRules.forEach((ruleIds, articleId) => {
            if (ruleIds.size > 1) {
                // This article ID is used in multiple rules
                this.duplicateArticleIds.set(articleId, Array.from(ruleIds));
                // Assign a color to this article ID
                this.articleIdColors.set(articleId, this.duplicateColors[colorIndex % this.duplicateColors.length]);
                colorIndex++;
            }
        });

        console.log('Duplicate article IDs found:', this.duplicateArticleIds.size);
        console.log('Duplicate article IDs:', Array.from(this.duplicateArticleIds.keys()));
    }

    // Get highlight color for a rule based on duplicate article IDs
    getRuleHighlightColor(rule: RuleModelType): string | null {
        if ((this.ruleType !== 'mating' && this.ruleType !== 'joint') || !this.duplicateArticleIds.size) {
            return null;
        }

        // Collect all article IDs from all cross sections in this rule
        const ruleArticleIds = new Set<string>();

        if (this.ruleType === 'mating') {
            const matingRule = rule as MatingRuleModel;
            if (!matingRule.id || !matingRule.crossSections) {
                return null;
            }
            matingRule?.crossSections?.forEach((crossSection: any) => {
                // Check articleIds array
                const articleIds = crossSection.articleIds || crossSection.ArticleIds || [];
                if (Array.isArray(articleIds)) {
                    articleIds.forEach((articleId: any) => {
                        if (articleId) {
                            const idStr = String(articleId).trim();
                            if (idStr) {
                                ruleArticleIds.add(idStr);
                            }
                        }
                    });
                }

                // Check articles array
                const articles = crossSection.articles || crossSection.Articles || [];
                if (Array.isArray(articles)) {
                    articles.forEach((article: any) => {
                        if (article) {
                            const articleId = article.id || article.Id || '';
                            if (articleId) {
                                const idStr = String(articleId).trim();
                                if (idStr) {
                                    ruleArticleIds.add(idStr);
                                }
                            }
                        }
                    });
                }
            });

        } else if (this.ruleType === 'joint') {
            const jointRule = rule as JointRuleModel;
            if (!jointRule.id || !jointRule.jointSections) {
                return null;
            }
            jointRule.jointSections.forEach((jointSection: any) => {
                // Check articleIds array
                const articleIds = jointSection.articleIds || jointSection.ArticleIds || [];
                if (Array.isArray(articleIds)) {
                    articleIds.forEach((articleId: any) => {
                        if (articleId) {
                            const idStr = String(articleId).trim();
                            if (idStr) {
                                ruleArticleIds.add(idStr);
                            }
                        }
                    });
                }

                // Check articles array
                const articles = jointSection.articles || jointSection.Articles || [];
                if (Array.isArray(articles)) {
                    articles.forEach((article: any) => {
                        if (article) {
                            const articleId = article.id || article.Id || '';
                            if (articleId) {
                                const idStr = String(articleId).trim();
                                if (idStr) {
                                    ruleArticleIds.add(idStr);
                                }
                            }
                        }
                    });
                }
            });

        }

        // Find the first duplicate article ID in this rule
        for (const articleId of ruleArticleIds) {
            if (this.duplicateArticleIds.has(articleId)) {
                return this.articleIdColors.get(articleId) || null;
            }
        }

        return null;
    }

    // Get duplicate article IDs for a specific rule
    getRuleDuplicateArticleIds(rule: RuleModelType): string[] {
        if (this.ruleType !== 'mating' && this.ruleType !== 'joint') {
            return [];
        }

        // Collect all article IDs from all cross sections in this rule
        const ruleArticleIds = new Set<string>();


        if (this.ruleType === 'mating') {
            const matingRule = rule as MatingRuleModel;
            if (!matingRule.id || !matingRule.crossSections) {
                return [];
            }
            matingRule.crossSections.forEach((crossSection: any) => {
                // Check articleIds array
                const articleIds = crossSection.articleIds || crossSection.ArticleIds || [];
                if (Array.isArray(articleIds)) {
                    articleIds.forEach((articleId: any) => {
                        if (articleId) {
                            const idStr = String(articleId).trim();
                            if (idStr) {
                                ruleArticleIds.add(idStr);
                            }
                        }
                    });
                }

                // Check articles array
                const articles = crossSection.articles || crossSection.Articles || [];
                if (Array.isArray(articles)) {
                    articles.forEach((article: any) => {
                        if (article) {
                            const articleId = article.id || article.Id || '';
                            if (articleId) {
                                const idStr = String(articleId).trim();
                                if (idStr) {
                                    ruleArticleIds.add(idStr);
                                }
                            }
                        }
                    });
                }
            });
        }

        else if (this.ruleType === 'joint') {
            const jointRule = rule as JointRuleModel;
            if (!jointRule.id || !jointRule.jointSections) {
                return [];
            }
            jointRule.jointSections.forEach((jointSection: any) => {
                // Check articleIds array
                const articleIds = jointSection.articleIds || jointSection.ArticleIds || [];
                if (Array.isArray(articleIds)) {
                    articleIds.forEach((articleId: any) => {
                        if (articleId) {
                            const idStr = String(articleId).trim();
                            if (idStr) {
                                ruleArticleIds.add(idStr);
                            }
                        }
                    });
                }

                // Check articles array
                const articles = jointSection.articles || jointSection.Articles || [];
                if (Array.isArray(articles)) {
                    articles.forEach((article: any) => {
                        if (article) {
                            const articleId = article.id || article.Id || '';
                            if (articleId) {
                                const idStr = String(articleId).trim();
                                if (idStr) {
                                    ruleArticleIds.add(idStr);
                                }
                            }
                        }
                    });
                }
            });
        }


        // Return only the article IDs that are duplicates
        const duplicates: string[] = [];
        ruleArticleIds.forEach((articleId) => {
            if (this.duplicateArticleIds.has(articleId)) {
                duplicates.push(articleId);
            }
        });

        return duplicates;
    }

    // Get all duplicate article IDs for the legend
    getDuplicateArticleIdsList(): Array<{ articleId: string; color: string; ruleCount: number }> {
        const result: Array<{ articleId: string; color: string; ruleCount: number }> = [];

        this.duplicateArticleIds.forEach((ruleIds, articleId) => {
            const color = this.articleIdColors.get(articleId) || '#E0E0E0';
            result.push({
                articleId,
                color,
                ruleCount: ruleIds.length
            });
        });

        // Sort by article ID for consistent display
        return result.sort((a, b) => a.articleId.localeCompare(b.articleId));
    }

    // Sorting methods
    onSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.applySorting();
    }

    applySorting(): void {
        if (!this.sortColumn) {
            return;
        }

        this.filteredRules.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (this.sortColumn) {
                case 'name':
                    aValue = this.getRuleName(a).toLowerCase();
                    bValue = this.getRuleName(b).toLowerCase();
                    break;
                case 'id':
                    aValue = (a as any).id || '';
                    bValue = (b as any).id || '';
                    break;
                case 'crossSections':
                    aValue = this.getCrossSectionsCount(a);
                    bValue = this.getCrossSectionsCount(b);
                    break;
                case 'jointSections':
                    aValue = this.getCrossSectionsCount(a);
                    bValue = this.getCrossSectionsCount(b);
                    break;
                case 'system':
                    aValue = this.getSystemName(this.getRuleSystemId(a) || '').toLowerCase();
                    bValue = this.getSystemName(this.getRuleSystemId(b) || '').toLowerCase();
                    break;
                case 'createdAt':
                    aValue = new Date((a as any).createdAt || 0).getTime();
                    bValue = new Date((b as any).createdAt || 0).getTime();
                    break;
                case 'updatedAt':
                    aValue = new Date((a as any).updatedAt || 0).getTime();
                    bValue = new Date((b as any).updatedAt || 0).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return this.sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return this.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    getSortIcon(column: string): string {
        if (this.sortColumn !== column) {
            return 'bi-arrow-down-up';
        }
        return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
    }

    goBack(): void {
        this.router.navigate(['/rules-dashboard']);
    }
}
